import { useState } from 'react'
import { FileText, MessageCircle, Trash2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { CommentText } from '@/components/common/CommentText'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { formatRelativeTime } from '@/utils/formatters'
import type { SkuWaveComment, SkuWaveLine } from './sku-model'

interface Props {
  line: SkuWaveLine
  comments: SkuWaveComment[]
  authorId: string
  onClose: () => void
  /** Resolves once the comment is saved; the draft is kept if it fails. */
  onPost: (content: string) => Promise<unknown>
  onDelete: (id: string) => void
  onRestoreFocus: () => void
}

export function SkuCommentsDrawer({
  line,
  comments,
  authorId,
  onClose,
  onPost,
  onDelete,
  onRestoreFocus,
}: Props) {
  const [content, setContent] = useState('')
  const [posting, setPosting] = useState(false)
  const canSubmit =
    !posting && content.trim().length > 0 && content.length <= 2000
  const post = () => {
    if (!canSubmit) return
    setPosting(true)
    onPost(content)
      .then(() => setContent(''))
      .catch(() => undefined) // the mutation already toasted the error
      .finally(() => setPosting(false))
  }
  return (
    <Sheet
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        onCloseAutoFocus={event => {
          event.preventDefault()
          onRestoreFocus()
        }}
      >
        <SheetHeader className="border-b p-4 pr-10">
          <SheetTitle>Comments</SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            <span className="font-medium text-foreground">
              {line.sku} · {line.type}
            </span>
            <br />
            {line.description}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {line.reviewNote && (
            <div className="rounded-md border bg-secondary/40 p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <FileText size={13} /> Review notes
              </p>
              <CommentText content={line.reviewNote} className="break-words" />
            </div>
          )}
          {!line.reviewNote && !comments.length && (
            <div className="flex flex-col items-center py-12 text-muted-foreground">
              <MessageCircle size={36} className="mb-3" />
              <p className="text-sm font-medium">No comments yet</p>
              <p className="mt-1 text-xs">Be the first to comment.</p>
            </div>
          )}
          {[...comments].reverse().map(comment => {
            const color = getAvatarColor(comment.authorId)
            return (
              <div key={comment.id} className="flex gap-3">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback
                    className={`${color.bg} ${color.text} text-xs`}
                  >
                    {getInitials(comment.authorName, comment.authorName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {comment.authorName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(comment.createdAt)}
                    </span>
                    {comment.authorId === authorId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-auto h-7 w-7 shrink-0"
                        aria-label="Delete comment"
                        onClick={() => onDelete(comment.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                  <CommentText
                    content={comment.content}
                    className="break-words"
                  />
                </div>
              </div>
            )
          })}
        </div>
        <div className="space-y-2 border-t p-4">
          <Textarea
            aria-label="Add a comment"
            placeholder="Add a comment..."
            value={content}
            rows={3}
            className="max-h-40 resize-none overflow-y-auto"
            aria-invalid={content.length > 2000}
            aria-describedby="sku-comment-length"
            onChange={event => setContent(event.target.value)}
            onKeyDown={event => {
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault()
                post()
              }
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">
              <p>Enter to send · Shift+Enter for newline</p>
              <p
                id="sku-comment-length"
                className={content.length > 2000 ? 'text-destructive' : ''}
              >
                {content.length.toLocaleString('en-US')} / 2,000 characters
              </p>
            </div>
            <Button size="sm" disabled={!canSubmit} onClick={post}>
              Post
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
